import { useDndMonitor } from '@dnd-kit/core';
import { cn } from '@tempo-io/sui-class-utils';
import { addDays, format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ITEM_HEIGHT,
  ITEM_HEIGHT_COMPACT,
  ITEM_LANE_HEIGHT,
  ITEM_LANE_HEIGHT_COMPACT,
} from './TimelineItem';
import { darkenTimelineBgColor, lightenTimelineBgColor } from './utils/timelineColorUtils';

type Preview = {
  itemId: number;
  dx: number;
  dy: number;
  width: number;
  height: number;
  color: string;
  name: string;
};

const PreviewBar = ({
  width,
  height,
  color,
  name,
  isCompact,
  isAnchor,
}: {
  width: number;
  height: number;
  color: string;
  name: string;
  isCompact: boolean;
  isAnchor: boolean;
}) => (
  <div
    className={cn(
      'shadow-lg flex items-center px-2 relative overflow-hidden',
      isCompact ? 'rounded-lg' : 'rounded-2xl',
      !isAnchor && 'opacity-80',
    )}
    style={{
      backgroundColor: color,
      width,
      height,
    }}
  >
    <div
      className={cn('absolute inset-0', isCompact ? 'rounded-lg' : 'rounded-2xl')}
      style={{ backgroundColor: darkenTimelineBgColor(color, 10) }}
    />
    <span
      className={cn('font-semibold truncate relative z-10', isCompact ? 'text-xs' : 'text-sm')}
      style={{ color: lightenTimelineBgColor(color, 80) }}
    >
      {name}
    </span>
  </div>
);

export const TimelineItemDragOverlayContent = ({
  name,
  color,
  startDate,
  endDate,
  dayWidth,
  isCompact,
  selectedCount,
  selectedPreviews,
}: {
  name: string;
  color: string;
  startDate: string;
  endDate: string;
  dayWidth: number;
  isCompact?: boolean;
  selectedCount?: number;
  selectedPreviews?: Preview[];
}) => {
  const { t } = useTranslation();
  const fallbackHeight = isCompact ? ITEM_HEIGHT_COMPACT : ITEM_HEIGHT;
  const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
  const itemTopOffset = isCompact ? 1 : laneHeight / 2 - fallbackHeight / 2 + 2;
  const [prospectiveDates, setProspectiveDates] = useState<{
    start: Date;
    end: Date;
    daysOffset: number;
  } | null>(null);

  useDndMonitor({
    onDragMove: (event) => {
      const { delta } = event;
      if (!delta || !startDate || !endDate) return;
      const daysOffset = Math.round(delta.x / dayWidth);
      if (daysOffset !== 0) {
        setProspectiveDates({
          start: addDays(new Date(startDate), daysOffset),
          end: addDays(new Date(endDate), daysOffset),
          daysOffset,
        });
      } else {
        setProspectiveDates(null);
      }
    },
    onDragEnd: () => setProspectiveDates(null),
  });

  const displayStartDate = prospectiveDates?.start || (startDate ? new Date(startDate) : null);
  const displayEndDate = prospectiveDates?.end || (endDate ? new Date(endDate) : null);
  const previews = selectedPreviews?.length
    ? selectedPreviews
    : [{ itemId: -1, dx: 0, dy: 0, width: 144, height: fallbackHeight, color, name }];
  const isBulk = previews.length > 1;
  const extraCount = (selectedCount ?? previews.length) - 1;

  return (
    <div className="relative" style={{ marginTop: itemTopOffset }}>
      {previews.map((p) => (
        <div
          key={p.itemId}
          className="absolute"
          style={{ left: p.dx, top: p.dy, width: p.width, height: p.height }}
        >
          <PreviewBar
            width={p.width}
            height={p.height}
            color={p.color}
            name={p.name}
            isCompact={!!isCompact}
            isAnchor={p.dx === 0 && p.dy === 0}
          />
        </div>
      ))}
      {isBulk && (
        <div className="absolute -top-2 -right-2 z-20 min-w-6 h-6 px-2 rounded-full bg-bg-info text-text-neutral-inverted text-xs font-semibold flex items-center justify-center shadow-md">
          {selectedCount ?? previews.length}
        </div>
      )}
      {displayStartDate && displayEndDate && (
        <div className="absolute -bottom-16 left-0 w-max bg-bg-surface-base text-text-neutral-primary border-2 text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-50">
          <div className="font-semibold">
            {name}
            {isBulk && extraCount > 0 && (
              <span className="ml-2 font-normal text-text-neutral-secondary">
                {t(($) => $.loopVisualization.timeline.dragOverlay.moreItems, {
                  count: extraCount,
                })}
              </span>
            )}
          </div>
          <div>
            {format(displayStartDate, 'MMM d, yyyy')} - {format(displayEndDate, 'MMM d, yyyy')}
          </div>
          {isBulk && prospectiveDates && prospectiveDates.daysOffset !== 0 && (
            <div className="text-text-neutral-secondary italic">
              {t(($) => $.loopVisualization.timeline.dragOverlay.bulkShift, {
                count: Math.abs(prospectiveDates.daysOffset),
                itemCount: selectedCount ?? previews.length,
                deltaSigned: `${prospectiveDates.daysOffset > 0 ? '+' : ''}${prospectiveDates.daysOffset}`,
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
